import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Skeleton } from './ui/skeleton';
import { SkeletonCard, SkeletonChart, SkeletonText, SkeletonMetric } from './ui/Skeletons';
import { Eye, Users, Activity, TrendingUp, Download, ArrowUp, Search, MapPin, Link, Globe, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import WorldMapECharts from './ui/WorldMapECharts';
import TopCountriesList from './ui/TopCountriesList';
import VisitsAndIPsPanel from './VisitsAndIPsPanel';
import TimeframeSelectorPopover from './ui/TimeframeSelectorPopover';
import DeviceComparisonCard from './DeviceComparisonCard';

export default function AnalyticsDashboard({ projectId }) {
  const [analytics, setAnalytics] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geoFilter, setGeoFilter] = useState('countries'); // countries, regions, cities, continents
  const [dateRange, setDateRange] = useState(7);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredContinent, setHoveredContinent] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [animateRings, setAnimateRings] = useState(false);
  
  // Drill-down Modal State
  const [drillDown, setDrillDown] = useState(null); // { type: 'device', value: 'Mobile' }
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  
  const mapRef = useRef(null);
  // Calculate metrics from dynamic data
  // These are computed early so hooks like useEffect for animations are declared consistently
  const filteredPages = analytics?.top_pages?.filter(page =>
    page.url.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const totalEvents = analytics?.total_events || 0;
  const totalPageviews = analytics?.total_pageviews || 0;
  const uniqueSessions = analytics?.unique_sessions || 0;
  const avgEventsPerSession = analytics?.avg_events_per_session || 0;

  // Bouncing rate metrics
  const bouncingRate = analytics?.bouncing_rate?.percentage || 0;
  const bouncingRateChange = typeof analytics?.bouncing_rate?.change === 'number' ? analytics.bouncing_rate.change : 0;

  // Visit duration metrics
  const avgVisitDuration = analytics?.avg_visit_duration?.minutes || 0;
  const visitDurationChange = typeof analytics?.avg_visit_duration?.change === 'number' ? analytics.avg_visit_duration.change : 0;

  // Dynamic percentage changes from backend
  const pageviewsChange = typeof analytics?.pageviews_change === 'number' ? analytics.pageviews_change : 0;
  const sessionsChange = typeof analytics?.sessions_change === 'number' ? analytics.sessions_change : 0;
  const eventsChange = typeof analytics?.events_change === 'number' ? analytics.events_change : 0;

  // Audience summary for card
  const topDevice = Object.entries(analytics?.devices || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
  const topContinent = analytics?.continents?.[0]?.name || 'N/A';

  // Browser distribution data
  const browsers = analytics?.browsers || {};
  const browserEntries = Object.entries(browsers);
  const topBrowser = browserEntries[0];
  const topBrowserPercent = topBrowser ? Math.round((topBrowser[1] / totalEvents) * 100) : 0;

  // Referrer data
  const referrers = analytics?.referrers || [];
  const topReferrer = referrers[0];
  const directTraffic = referrers.filter(r => r.source === 'Direct' || r.source === '')[0];

  // Calculate composition percentages (ensuring they add up to ~100)
  const pageviewsPercent = totalEvents > 0 ? Math.round((totalPageviews / totalEvents) * 100) : 0;
  const sessionsPercent = totalEvents > 0 ? Math.round((uniqueSessions / totalEvents) * 100) : 0;
  let otherPercent = totalEvents > 0 ? 100 - pageviewsPercent - sessionsPercent : 0;
  if (otherPercent < 0) {
    // If rounding causes slight negative, clamp at 0 and adjust last value to fit
    otherPercent = 0;
  }

  useEffect(() => {
    // animate rings every time the percentages change
    setAnimateRings(false);
    const id = setTimeout(() => setAnimateRings(true), 30);
    return () => clearTimeout(id);
  }, [pageviewsPercent, sessionsPercent, otherPercent]);

  useEffect(() => {
    fetchAnalytics();
  }, [projectId, dateRange]);

  // Debug: Log when geoFilter changes
  useEffect(() => {
    console.log('GeoFilter changed to:', geoFilter);
  }, [geoFilter]);

  const fetchDrillDown = async (type, value) => {
    setDrillDown({ type, value });
    setDrillDownLoading(true);
    setDrillDownData(null);
    try {
      const response = await axios.get(`/analytics/${projectId}/drilldown?days=${dateRange}&filter_type=${type}&filter_value=${encodeURIComponent(value)}`);
      setDrillDownData(response.data);
    } catch (error) {
      console.error('Failed to load drill-down data:', error);
      toast.error('Failed to load segment details');
      setDrillDown(null);
    } finally {
      setDrillDownLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [overviewRes, geoRes] = await Promise.all([
        axios.get(`/analytics/${projectId}/overview?days=${dateRange}`),
        axios.get(`/analytics/${projectId}/geo?days=${dateRange}`)
      ]);
      console.log('Geo data received:', geoRes.data);
      console.log('Geo filter:', geoFilter);
      console.log('Data for filter:', geoRes.data?.[geoFilter]);
      setAnalytics(overviewRes.data);
      setGeoData(geoRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      toast.info('Generating CSV report...');
      const response = await axios.get(`/analytics/${projectId}/export?days=${dateRange}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `analytics_report_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('CSV report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export CSV report');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6 min-h-screen transition-colors" data-testid="analytics-loading">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row flex-wrap justify-between items-start sm:items-center gap-4">
          <div>
            <SkeletonText width="w-64" height="h-10" className="mb-3" />
            <SkeletonText width="w-80" height="h-5" />
          </div>
          <div className="flex gap-3">
            <SkeletonText width="w-32" height="h-10" />
            <SkeletonText width="w-32" height="h-10" />
          </div>
        </div>

        {/* Metrics Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <SkeletonMetric key={i} />
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <SkeletonCard>
              <SkeletonText width="w-48" height="h-6" className="mb-8" />
              <SkeletonChart type="bar" height="h-[300px]" />
            </SkeletonCard>
          </div>
          <div>
            <SkeletonCard>
              <SkeletonText width="w-48" height="h-6" className="mb-8" />
              <SkeletonChart type="circle" height="h-[300px]" />
            </SkeletonCard>
          </div>
        </div>

        {/* Tables/Lists Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
           <div>
             <SkeletonCard>
               <SkeletonText width="w-48" height="h-6" className="mb-8" />
               <SkeletonChart type="table" height="h-[250px]" />
             </SkeletonCard>
           </div>
           <div>
             <SkeletonCard>
               <SkeletonText width="w-48" height="h-6" className="mb-8" />
               <SkeletonChart type="table" height="h-[250px]" />
             </SkeletonCard>
           </div>
        </div>
      </div>
    );
  }



  return (
    <div className="space-y-4 md:space-y-5 lg:space-y-6 p-2 md:p-4 lg:p-6 bg-slate-50 dark:bg-background min-h-screen transition-colors" data-testid="analytics-dashboard">
      {/* Header with Controls */}
      <div className="flex flex-col md:flex-row flex-wrap justify-between items-start md:items-center gap-3 md:gap-4">
        <div>
          <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white mb-1.5 md:mb-2">Analytics Dashboard</h3>
          <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400">Track your website performance and visitor insights</p>
        </div>
        <div className="flex flex-wrap gap-2.5 md:gap-3 items-center">
          <TimeframeSelectorPopover
            value={dateRange}
            onChange={(val) => setDateRange(Number(val))}
          />
          <Button
            data-testid="export-csv-button"
            onClick={handleExportCSV}
            className="bg-[#FEAB3D] text-[#021426] px-4 md:px-6 py-2 rounded-none font-medium flex items-center gap-2 hover:bg-[#e8960a] hover:text-[#021426] dark:hover:text-[#021426] text-xs md:text-sm h-11"
          >
            <Download className="w-3.5 h-3.5 md:w-4 md:h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
        {/* Pageviews Card */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-5 lg:p-6 border border-slate-100 dark:border-border transition-colors" data-testid="metric-pageviews">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-xs md:text-sm font-medium">Total Pageviews</p>
              <div className="flex flex-wrap items-end gap-2 mt-1">
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                  {totalPageviews.toLocaleString()}
                </div>
                <div className={`flex items-center gap-1 text-sm mb-1 ${pageviewsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <ArrowUp className="w-4 h-4" />
                  <span>{pageviewsChange > 0 ? '+' : ''}{pageviewsChange}%</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Total number of page views across all sessions</p>
            </div>
            <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-[#FEAB3D]/20 dark:bg-[#FEAB3D]/40 rounded-xl flex items-center justify-center">
              <Eye className="w-6 h-6 text-[#FEAB3D] dark:text-blue-400" />
            </div>
          </div>
        </Card>

        {/* Sessions Card */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">Unique Sessions</p>
              <div className="flex flex-wrap items-end gap-2 mt-1">
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                  {uniqueSessions.toLocaleString()}
                </div>
                <div className={`flex items-center gap-1 text-sm mb-1 ${sessionsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <ArrowUp className="w-4 h-4" />
                  <span>{sessionsChange > 0 ? '+' : ''}{sessionsChange}%</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Individual user sessions tracked on your site</p>
            </div>
            <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 dark:bg-orange-900/40 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </Card>

        {/* Total Events Card */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">Total Events</p>
              <div className="flex flex-wrap items-end gap-2 mt-1">
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                  {totalEvents.toLocaleString()}
                </div>
                <div className={`flex items-center gap-1 text-sm mb-1 ${eventsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <ArrowUp className="w-4 h-4" />
                  <span>{eventsChange > 0 ? '+' : ''}{eventsChange}%</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Average {avgEventsPerSession} events per session</p>
            </div>
            <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-[#FFF6E5] dark:bg-[#021426]/20 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-[#021426] dark:text-[#FEAB3D]" />
            </div>
          </div>
        </Card>

        {/* Bouncing Rate Card */}
        <Card data-testid="metric-bouncing-rate" className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">Bouncing Rate</p>
              <div className="flex flex-wrap items-end gap-2 mt-1">
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                  {bouncingRate.toFixed(1)}%
                </div>
                <div className={`flex items-center gap-1 text-sm mb-1 ${bouncingRateChange <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <ArrowUp className={`w-4 h-4 ${bouncingRateChange <= 0 ? 'rotate-180' : ''}`} />
                  <span>{bouncingRateChange > 0 ? '+' : ''}{bouncingRateChange}%</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Percentage of sessions that bounced from your site</p>
            </div>
            <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-red-100 dark:bg-red-900/40 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </Card>

        {/* Audience Insights Card */}
        <Card data-testid="metric-audience" className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">Audience Insights</p>
              <div className="mt-1">
                <div className="text-2xl font-bold text-slate-900 dark:text-white">
                  {topDevice} • {topContinent}
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Devices, geography, and demographics overview</p>
            </div>
            <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-[#FEAB3D]/20 dark:bg-[#FEAB3D]/40 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-[#FEAB3D] dark:text-purple-400" />
            </div>
          </div>
        </Card>

        {/* Visit Duration Card */}
        <Card data-testid="metric-visit-duration" className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">Visit Duration</p>
              <div className="flex flex-wrap items-end gap-2 mt-1">
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                  {avgVisitDuration.toFixed(1)}m
                </div>
                <div className={`flex items-center gap-1 text-sm mb-1 ${visitDurationChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <ArrowUp className="w-4 h-4" />
                  <span>{visitDurationChange > 0 ? '+' : ''}{visitDurationChange}%</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Average time spent by visitors on your site</p>
            </div>
            <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-cyan-100 dark:bg-cyan-900/40 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Countries Map Analytics Section */}
      <div className="grid grid-cols-1 xl:grid-cols-10 gap-3 md:gap-6 mb-3 md:mb-6 items-stretch">
        <div className="min-w-0 xl:col-span-6 flex flex-col h-full relative">
          <WorldMapECharts
            data={geoData?.countries || []}
            filterType="countries"
          />
        </div>
        <div className="min-w-0 xl:col-span-4 flex flex-col h-full">
          <TopCountriesList
            countries={geoData || {}}
            title="Top Locations"
            geoFilter={geoFilter}
            onGeoFilterChange={setGeoFilter}
          />
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 md:gap-6">
        {/* Left Column - Daily Traffic Chart */}
        <Card className="xl:col-span-2 bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Traffic Over Time</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Daily traffic breakdown - Shows total events per day over selected period</p>
          {analytics?.daily_traffic && analytics.daily_traffic.length > 0 ? (
            <div className="space-y-4 overflow-x-auto">
              {/* SVG Line Chart with smooth curves */}
              <svg width="100%" height="280" viewBox="0 0 800 280" className="w-full min-w-[600px]" style={{ overflow: 'visible' }}>
                {/* Grid background */}
                <defs>
                  <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                  </linearGradient>
                </defs>

                {/* Horizontal grid lines */}
                {[0, 1, 2, 3, 4].map((i) => (
                  <line key={`grid-${i}`} x1="40" y1={40 + (i * 56)} x2="780" y2={40 + (i * 56)} stroke="#e2e8f0" strokeWidth="1" />
                ))}

                {/* Y-axis */}
                <line x1="40" y1="20" x2="40" y2="240" stroke="#cbd5e1" strokeWidth="2" />
                {/* X-axis */}
                <line x1="40" y1="240" x2="780" y2="240" stroke="#cbd5e1" strokeWidth="2" />

                {/* Y-axis labels */}
                {[0, 1, 2, 3, 4].map((i) => {
                  const maxCount = Math.max(...analytics.daily_traffic.map(d => d.count));
                  const label = Math.round((maxCount / 4) * (4 - i));
                  return (
                    <text key={`label-${i}`} x="30" y={47 + (i * 56)} fontSize="10" fill="#64748b" textAnchor="end">
                      {label}
                    </text>
                  );
                })}

                {/* Generate line path and area */}
                {(() => {
                  const maxCount = Math.max(...analytics.daily_traffic.map(d => d.count));
                  const dataPoints = analytics.daily_traffic.map((day, index) => {
                    const x = 60 + (index / (analytics.daily_traffic.length - 1 || 1)) * 720;
                    const y = 240 - ((day.count / maxCount) * 200);
                    return { x, y, ...day };
                  });

                  // Build smooth curve path
                  let pathD = `M ${dataPoints[0].x} ${dataPoints[0].y}`;
                  for (let i = 1; i < dataPoints.length; i++) {
                    const prev = dataPoints[i - 1];
                    const curr = dataPoints[i];
                    const cp1x = prev.x + 40;
                    const cp1y = prev.y;
                    const cp2x = curr.x - 40;
                    const cp2y = curr.y;
                    pathD += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${curr.x} ${curr.y}`;
                  }

                  return (
                    <>
                      {/* Area under curve */}
                      <path d={`${pathD} L ${dataPoints[dataPoints.length - 1].x} 240 L ${dataPoints[0].x} 240 Z`}
                        fill="url(#areaGradient)" />

                      {/* Line */}
                      <path d={pathD} stroke="#3b82f6" strokeWidth="3" fill="none" />

                      {/* Data points */}
                      {dataPoints.map((point, index) => (
                        <g key={`point-${index}`}>
                          <circle cx={point.x} cy={point.y} r="5" fill="#3b82f6" opacity="0" className="hover:opacity-100 transition-opacity" />
                          <circle cx={point.x} cy={point.y} r="3" fill="#fff" stroke="#3b82f6" strokeWidth="2" />
                          {/* Tooltip on hover */}
                          <title>{`${point.date}: ${point.count} events`}</title>
                        </g>
                      ))}
                    </>
                  );
                })()}

                {/* X-axis labels */}
                {analytics.daily_traffic.map((day, index) => {
                  if (index % Math.ceil(analytics.daily_traffic.length / 5) === 0 || index === analytics.daily_traffic.length - 1) {
                    const x = 60 + (index / (analytics.daily_traffic.length - 1 || 1)) * 720;
                    return (
                      <text key={`x-label-${index}`} x={x} y="260" fontSize="10" fill="#64748b" textAnchor="middle">
                        {day.date.slice(-2)}
                      </text>
                    );
                  }
                  return null;
                })}
              </svg>

              <div className="flex gap-6 text-xs text-slate-600 dark:text-slate-400 mt-4 px-4 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <div><span className="inline-block w-3 h-3 bg-blue-400 rounded mr-2"></span>Events per Day</div>
              </div>
            </div>
          ) : (
            <p className="text-slate-600 dark:text-slate-400 text-center py-8">No traffic data available yet</p>
          )}
        </Card>

        {/* Right Column - Event Distribution */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Traffic Composition</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Percentage breakdown of traffic types</p>
          <div className="flex flex-col items-center">
            {/* Concentric Rings Representation */}
            <div className="relative w-40 h-40 mb-6">
              <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                {(() => {
                  const metrics = [
                    { key: 'pageviews', percent: pageviewsPercent, color: '#021426', testId: 'ring-pageviews' },
                    { key: 'sessions', percent: sessionsPercent, color: '#f97316', testId: 'ring-sessions' },
                    { key: 'other', percent: otherPercent, color: '#60a5fa', testId: 'ring-other' }
                  ];
                  const sorted = metrics.slice().sort((a, b) => b.percent - a.percent);
                  const radii = [15.915, 12.5, 9];
                  const strokeWidths = [1.6, 1.8, 2.0];

                  return sorted.map((m, idx) => {
                    const r = radii[idx];
                    const c = 2 * Math.PI * r;
                    const finalLen = (c * m.percent) / 100;
                    const currentLen = animateRings ? finalLen : 0;
                    const dash = `${currentLen} ${c - currentLen}`;
                    const strokeW = strokeWidths[idx];
                    return (
                      <g key={m.key}>
                        <circle cx="18" cy="18" r={r} fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-100 dark:text-slate-800" />
                        <circle data-testid={m.testId} cx="18" cy="18" r={r} fill="none" stroke={m.color} strokeWidth={strokeW} strokeDasharray={dash} strokeLinecap="round" style={{ transition: `stroke-dasharray ${700 + idx * 50}ms cubic-bezier(.22,.68,0,1)` }}>
                          <title>{`${m.key.charAt(0).toUpperCase() + m.key.slice(1)}: ${m.percent}%`}</title>
                        </circle>
                      </g>
                    );
                  });
                })()}
              </svg>
            </div>
            <div className="space-y-2 w-full text-sm">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-[#021426] dark:bg-[#FEAB3D] rounded-full"></span>
                  <span className="text-slate-600 dark:text-slate-400">Pageviews</span>
                </div>
                <span className="font-medium text-slate-900 dark:text-white">{pageviewsPercent}%</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-[#f97316] rounded-full"></span>
                  <span className="text-slate-600 dark:text-slate-400">Sessions</span>
                </div>
                <span className="font-medium text-slate-900 dark:text-white">{sessionsPercent}%</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-[#60a5fa] rounded-full"></span>
                  <span className="text-slate-600 dark:text-slate-400">Other Events</span>
                </div>
                <span className="font-medium text-slate-900 dark:text-white">{otherPercent}%</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Visits Chart + Top Client IPs */}
      <VisitsAndIPsPanel projectId={projectId} dateRange={dateRange} />

      {/* Bottom Section - Pages and Breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 md:gap-6">
        {/* Top Pages */}
        <Card className="xl:col-span-2 bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Most Visited Pages</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">Top 5 pages with highest traffic</p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search pages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#021426] w-full dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
          {filteredPages && filteredPages.length > 0 ? (
            <div className="space-y-3">
              {filteredPages.map((page, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 bg-[#FEAB3D] dark:bg-[#021426]/20 rounded-full flex items-center justify-center text-[#021426] dark:text-[#FEAB3D] text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-700 dark:text-slate-300 font-medium truncate text-sm">{page.url}</p>
                    </div>
                  </div>
                  <div className="text-right ml-4 flex-shrink-0">
                    <p className="font-bold text-slate-900 dark:text-white">{page.views}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">views</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-600 dark:text-slate-400 text-center py-8">No pages match your search</p>
          )}
        </Card>

        {/* Browser Distribution */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Browser Distribution</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Top browsers used by visitors</p>
          <div className="space-y-4">
            {browserEntries.length > 0 ? (
              browserEntries.map(([browser, count], index) => {
                const percent = Math.round((count / totalEvents) * 100);
                return (
                  <div key={index} className="space-y-1 cursor-pointer group" onClick={() => fetchDrillDown('browser', browser)}>
                    <div className="flex justify-between text-xs group-hover:text-[#021426] dark:group-hover:text-[#FEAB3D] transition-colors">
                      <span className="text-slate-600 dark:text-slate-400 font-medium group-hover:text-[#021426] dark:group-hover:text-[#FEAB3D]">{browser}</span>
                      <span className="font-semibold text-slate-900 dark:text-white group-hover:text-[#021426] dark:group-hover:text-[#FEAB3D]">{percent}%</span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-[#FEAB3D] dark:bg-[#FEAB3D]/80 h-full rounded-full transition-all"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-slate-500 dark:text-slate-400 text-center py-4">No browser data available</p>
            )}
          </div>
        </Card>
      </div>

      {/* Traffic Sources & Device Breakdown Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6 items-stretch">
        {/* Column 1 - Top Referrers */}
        <Card className="bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none min-w-0 p-4 md:p-6 border border-slate-100 dark:border-border transition-colors h-full flex flex-col justify-between min-h-[280px] sm:min-h-[450px]">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Top Referrers</h3>
            <div className="space-y-3">
              {(() => {
                const hasReal = totalPageviews > 0 && referrers && referrers.length > 0;
                const mapProvider = (src) => {
                  if (!src) return 'Direct';
                  const s = src.toLowerCase();
                  if (s.includes('mail.google') || s.includes('gmail')) return 'Gmail';
                  if (s.includes('outlook') || s.includes('office') || s.includes('live.com') || s.includes('hotmail')) return 'Outlook/Hotmail';
                  if (s.includes('yahoo')) return 'Yahoo Mail';
                  if (s.includes('facebook')) return 'Facebook';
                  if (s.includes('twitter') || s.includes('t.co')) return 'Twitter';
                  if (s.includes('linkedin')) return 'LinkedIn';
                  if (s.includes('google')) return 'Google';
                  if (s.includes('bing')) return 'Bing';
                  if (s.includes('duckduckgo') || s.includes('duck')) return 'DuckDuckGo';
                  if (s === 'direct' || s === 'direct / none') return 'Direct';
                  try {
                    const host = (src || '').replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
                    return host || src;
                  } catch (e) {
                    return src;
                  }
                };

                let sourceList = [];
                if (hasReal) {
                  const grouped = {};
                  referrers.forEach(r => {
                    const key = mapProvider(r.source || '');
                    grouped[key] = (grouped[key] || 0) + (r.count || 0);
                  });
                  sourceList = Object.entries(grouped).map(([k, v]) => ({ source: k, count: v }));
                  sourceList.sort((a, b) => b.count - a.count);
                }

                if (sourceList.length === 0) {
                  return <p className="text-slate-500 dark:text-slate-400 text-center py-8">No referrer data available</p>;
                }

                const refTotal = totalPageviews > 0 ? totalPageviews : sourceList.reduce((s, it) => s + it.count, 0) || 1;

                return sourceList.slice(0, 5).map((r, idx) => {
                  const pct = Math.round((r.count / refTotal) * 100);
                  const display = r.source;
                  return (
                    <div key={idx} 
                         className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 transition-colors cursor-pointer hover:border-[#021426] dark:hover:border-[#FEAB3D]"
                         onClick={() => fetchDrillDown('referrer', display)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-[#FEAB3D] dark:bg-[#021426]/20 rounded-full flex items-center justify-center text-[#021426] dark:text-[#FEAB3D] text-xs font-bold flex-shrink-0">
                          {display ? display[0].toUpperCase() : 'R'}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{display}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{r.count} visits</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-900 dark:text-white">{pct}%</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">of traffic</div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </Card>

        {/* Column 2 - Device Type Distribution (Desktop vs. tablet vs. mobile) */}
        <DeviceComparisonCard
          devices={analytics?.devices || {}}
          dailyDevices={analytics?.daily_devices || []}
          dateRange={dateRange}
        />
      </div>

      {/* Drill-down Modal */}
      {drillDown && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setDrillDown(null)}>
          <Card 
            className="w-full max-w-4xl max-h-[calc(100dvh-2rem)] bg-white dark:bg-card border-slate-200 dark:border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white capitalize flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#021426] dark:text-[#FEAB3D]" /> {drillDown.type} Segment: {drillDown.value}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Granular behavior analysis for this specific segment over the last {dateRange} days.
                </p>
              </div>
              <Button variant="ghost" className="rounded-none w-10 h-10 p-0 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500" onClick={() => setDrillDown(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 bg-white dark:bg-card custom-scrollbar">
              {drillDownLoading ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-4 select-none">
                  <div className="relative w-10 h-10">
                    <div className="absolute inset-0 rounded-full border-4 border-slate-200 dark:border-slate-800"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-[#021426] dark:border-[#FEAB3D] border-t-transparent animate-spin"></div>
                  </div>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 tracking-wide">Analyzing segment data matrix...</p>
                </div>
              ) : drillDownData ? (
                <div className="space-y-8 pb-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-100 dark:border-slate-700/60 shadow-sm">
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Segment Events</p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white flex items-end gap-2 text-[#021426] dark:text-[#FEAB3D]">
                        {drillDownData.matched_events.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-100 dark:border-slate-700/60 shadow-sm">
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Segment Pageviews</p>
                      <p className="text-2xl font-bold text-slate-900 dark:text-white flex items-end gap-2 text-[#FEAB3D] dark:text-blue-400">
                        {drillDownData.matched_pageviews.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Top Pages for this segment */}
                    <div>
                      <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
                        <Eye className="w-4 h-4 text-[#FEAB3D]"/> Content Affinity
                      </h4>
                      <div className="space-y-2">
                        {drillDownData.top_pages.map((p, i) => (
                           <div key={i} className="flex justify-between items-center text-sm p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg group border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-colors">
                             <div className="flex items-center gap-3 overflow-hidden">
                               <span className="w-5 text-center text-xs font-bold text-slate-400">{i+1}</span>
                               <span className="text-slate-700 dark:text-slate-300 truncate pr-4 font-medium" title={p.url || '/'}>{p.url || '/'}</span>
                             </div>
                             <span className="font-bold text-slate-900 dark:text-white flex-shrink-0 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">{p.views}</span>
                           </div>
                        ))}
                        {drillDownData.top_pages.length === 0 && <p className="text-slate-500 text-sm italic py-4">No page data recorded for this segment.</p>}
                      </div>
                    </div>

                    {/* Top Regions or Referrers based on what was NOT filtered */}
                    <div>
                      <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-slate-800">
                        <MapPin className="w-4 h-4 text-orange-500"/> Geographic Distribution
                      </h4>
                      <div className="space-y-2">
                        {drillDownData.top_countries.map((c, i) => (
                           <div key={i} className="flex justify-between items-center text-sm p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg group border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-colors">
                             <div className="flex items-center gap-3 overflow-hidden">
                               <span className="w-5 text-center text-xs font-bold text-slate-400">{i+1}</span>
                               <span className="text-slate-700 dark:text-slate-300 truncate pr-4 font-medium">{c.country}</span>
                             </div>
                             <span className="font-bold text-slate-900 dark:text-white flex-shrink-0 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">{c.count}</span>
                           </div>
                        ))}
                        {drillDownData.top_countries.length === 0 && <p className="text-slate-500 text-sm italic py-4">No region data recorded for this segment.</p>}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 text-red-500 dark:text-red-400 font-medium">Failed to construct data matrix. Please try again.</div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
